import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Button, Table } from 'reactstrap';
import { JhiItemCount, JhiPagination, Translate, getPaginationState } from 'react-jhipster';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort, faSortDown, faSortUp } from '@fortawesome/free-solid-svg-icons';
import { ASC, DESC, ITEMS_PER_PAGE, SORT } from 'app/shared/util/pagination.constants';
import { overridePaginationStateWithQueryParams } from 'app/shared/util/entity-utils';
import { useAppDispatch, useAppSelector } from 'app/config/store';

import { getEntities } from './age-by-single-year-of-age.reducer';

export const AgeBySingleYearOfAge = () => {
  const dispatch = useAppDispatch();

  const pageLocation = useLocation();
  const navigate = useNavigate();

  const [paginationState, setPaginationState] = useState(
    overridePaginationStateWithQueryParams(getPaginationState(pageLocation, ITEMS_PER_PAGE, 'id'), pageLocation.search),
  );

  const ageBySingleYearOfAgeList = useAppSelector(state => state.ageBySingleYearOfAge.entities);
  const loading = useAppSelector(state => state.ageBySingleYearOfAge.loading);
  const totalItems = useAppSelector(state => state.ageBySingleYearOfAge.totalItems);

  const getAllEntities = () => {
    dispatch(
      getEntities({
        page: paginationState.activePage - 1,
        size: paginationState.itemsPerPage,
        sort: `${paginationState.sort},${paginationState.order}`,
      }),
    );
  };

  const sortEntities = () => {
    getAllEntities();
    const endURL = `?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`;
    if (pageLocation.search !== endURL) {
      navigate(`${pageLocation.pathname}${endURL}`);
    }
  };

  useEffect(() => {
    sortEntities();
  }, [paginationState.activePage, paginationState.order, paginationState.sort]);

  useEffect(() => {
    const params = new URLSearchParams(pageLocation.search);
    const page = params.get('page');
    const sort = params.get(SORT);
    if (page && sort) {
      const sortSplit = sort.split(',');
      setPaginationState({
        ...paginationState,
        activePage: +page,
        sort: sortSplit[0],
        order: sortSplit[1],
      });
    }
  }, [pageLocation.search]);

  const sort = p => () => {
    setPaginationState({
      ...paginationState,
      order: paginationState.order === ASC ? DESC : ASC,
      sort: p,
    });
  };

  const handlePagination = currentPage =>
    setPaginationState({
      ...paginationState,
      activePage: currentPage,
    });

  const handleSyncList = () => {
    sortEntities();
  };

  const getSortIconByFieldName = (fieldName: string) => {
    const sortFieldName = paginationState.sort;
    const order = paginationState.order;
    if (sortFieldName !== fieldName) {
      return faSort;
    }
    return order === ASC ? faSortUp : faSortDown;
  };

  return (
    <div>
      <h2 id="age-by-single-year-of-age-heading" data-cy="AgeBySingleYearOfAgeHeading">
        <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.home.title">Age By Single Year Of Ages</Translate>
        <div className="d-flex justify-content-end">
          <Button className="me-2" color="info" onClick={handleSyncList} disabled={loading}>
            <FontAwesomeIcon icon="sync" spin={loading} />{' '}
            <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.home.refreshListLabel">Refresh List</Translate>
          </Button>
          <Link
            to="/age-by-single-year-of-age/new"
            className="btn btn-primary jh-create-entity"
            id="jh-create-entity"
            data-cy="entityCreateButton"
          >
            <FontAwesomeIcon icon="plus" />
            &nbsp;
            <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.home.createLabel">Create new Age By Single Year Of Age</Translate>
          </Link>
        </div>
      </h2>
      <div className="table-responsive">
        {ageBySingleYearOfAgeList && ageBySingleYearOfAgeList.length > 0 ? (
          <Table responsive>
            <thead>
              <tr>
                <th className="hand" onClick={sort('id')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.id">ID</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('id')} />
                </th>
                <th className="hand" onClick={sort('geographicLevel')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.geographicLevel">Geographic Level</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographicLevel')} />
                </th>
                <th className="hand" onClick={sort('date')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.date">Date</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('date')} />
                </th>
                <th className="hand" onClick={sort('geography')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.geography">Geography</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geography')} />
                </th>
                <th className="hand" onClick={sort('geographyCode')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.geographyCode">Geography Code</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('geographyCode')} />
                </th>
                <th className="hand" onClick={sort('totalValue')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.totalValue">Total Value</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('totalValue')} />
                </th>
                <th className="hand" onClick={sort('aged4AndUnder')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.aged4AndUnder">Aged 4 And Under</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('aged4AndUnder')} />
                </th>
                <th className="hand" onClick={sort('underOneYear')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.underOneYear">Under One Year</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('underOneYear')} />
                </th>
                <th className="hand" onClick={sort('aged1Year')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.aged1Year">Aged 1 Year</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('aged1Year')} />
                </th>
                <th className="hand" onClick={sort('aged2Years')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.aged2Years">Aged 2 Years</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('aged2Years')} />
                </th>
                <th className="hand" onClick={sort('aged3Years')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.aged3Years">Aged 3 Years</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('aged3Years')} />
                </th>
                <th className="hand" onClick={sort('aged4Years')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.aged4Years">Aged 4 Years</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('aged4Years')} />
                </th>
                <th className="hand" onClick={sort('aged5To9Years')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.aged5To9Years">Aged 5 To 9 Years</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('aged5To9Years')} />
                </th>
                <th className="hand" onClick={sort('aged10To15Years')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.aged10To15Years">Aged 10 To 15 Years</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('aged10To15Years')} />
                </th>
                <th className="hand" onClick={sort('aged16To19Years')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.aged16To19Years">Aged 16 To 19 Years</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('aged16To19Years')} />
                </th>
                <th className="hand" onClick={sort('aged20To24Years')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.aged20To24Years">Aged 20 To 24 Years</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('aged20To24Years')} />
                </th>
                <th className="hand" onClick={sort('aged25To34Years')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.aged25To34Years">Aged 25 To 34 Years</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('aged25To34Years')} />
                </th>
                <th className="hand" onClick={sort('aged35To49Years')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.aged35To49Years">Aged 35 To 49 Years</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('aged35To49Years')} />
                </th>
                <th className="hand" onClick={sort('aged50To64Years')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.aged50To64Years">Aged 50 To 64 Years</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('aged50To64Years')} />
                </th>
                <th className="hand" onClick={sort('aged65To74Years')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.aged65To74Years">Aged 65 To 74 Years</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('aged65To74Years')} />
                </th>
                <th className="hand" onClick={sort('aged75To84Years')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.aged75To84Years">Aged 75 To 84 Years</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('aged75To84Years')} />
                </th>
                <th className="hand" onClick={sort('aged85AndOver')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.aged85AndOver">Aged 85 And Over</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('aged85AndOver')} />
                </th>
                <th className="hand" onClick={sort('aged100AndOver')}>
                  <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.aged100AndOver">Aged 100 And Over</Translate>{' '}
                  <FontAwesomeIcon icon={getSortIconByFieldName('aged100AndOver')} />
                </th>
                <th />
              </tr>
            </thead>
            <tbody>
              {ageBySingleYearOfAgeList.map((ageBySingleYearOfAge, i) => (
                <tr key={`entity-${i}`} data-cy="entityTable">
                  <td>
                    <Button tag={Link} to={`/age-by-single-year-of-age/${ageBySingleYearOfAge.id}`} color="link" size="sm">
                      {ageBySingleYearOfAge.id}
                    </Button>
                  </td>
                  <td>
                    <Translate contentKey={`apiLocationApp.GeographicLevel.${ageBySingleYearOfAge.geographicLevel}`} />
                  </td>
                  <td>{ageBySingleYearOfAge.date}</td>
                  <td>{ageBySingleYearOfAge.geography}</td>
                  <td>{ageBySingleYearOfAge.geographyCode}</td>
                  <td>{ageBySingleYearOfAge.totalValue}</td>
                  <td>{ageBySingleYearOfAge.aged4AndUnder}</td>
                  <td>{ageBySingleYearOfAge.underOneYear}</td>
                  <td>{ageBySingleYearOfAge.aged1Year}</td>
                  <td>{ageBySingleYearOfAge.aged2Years}</td>
                  <td>{ageBySingleYearOfAge.aged3Years}</td>
                  <td>{ageBySingleYearOfAge.aged4Years}</td>
                  <td>{ageBySingleYearOfAge.aged5To9Years}</td>
                  <td>{ageBySingleYearOfAge.aged10To15Years}</td>
                  <td>{ageBySingleYearOfAge.aged16To19Years}</td>
                  <td>{ageBySingleYearOfAge.aged20To24Years}</td>
                  <td>{ageBySingleYearOfAge.aged25To34Years}</td>
                  <td>{ageBySingleYearOfAge.aged35To49Years}</td>
                  <td>{ageBySingleYearOfAge.aged50To64Years}</td>
                  <td>{ageBySingleYearOfAge.aged65To74Years}</td>
                  <td>{ageBySingleYearOfAge.aged75To84Years}</td>
                  <td>{ageBySingleYearOfAge.aged85AndOver}</td>
                  <td>{ageBySingleYearOfAge.aged100AndOver}</td>
                  <td className="text-end">
                    <div className="btn-group flex-btn-group-container">
                      <Button
                        tag={Link}
                        to={`/age-by-single-year-of-age/${ageBySingleYearOfAge.id}`}
                        color="info"
                        size="sm"
                        data-cy="entityDetailsButton"
                      >
                        <FontAwesomeIcon icon="eye" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.view">View</Translate>
                        </span>
                      </Button>
                      <Button
                        tag={Link}
                        to={`/age-by-single-year-of-age/${ageBySingleYearOfAge.id}/edit?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`}
                        color="primary"
                        size="sm"
                        data-cy="entityEditButton"
                      >
                        <FontAwesomeIcon icon="pencil-alt" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.edit">Edit</Translate>
                        </span>
                      </Button>
                      <Button
                        onClick={() =>
                          (window.location.href = `/age-by-single-year-of-age/${ageBySingleYearOfAge.id}/delete?page=${paginationState.activePage}&sort=${paginationState.sort},${paginationState.order}`)
                        }
                        color="danger"
                        size="sm"
                        data-cy="entityDeleteButton"
                      >
                        <FontAwesomeIcon icon="trash" />{' '}
                        <span className="d-none d-md-inline">
                          <Translate contentKey="entity.action.delete">Delete</Translate>
                        </span>
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          !loading && (
            <div className="alert alert-warning">
              <Translate contentKey="apiLocationApp.ageBySingleYearOfAge.home.notFound">No Age By Single Year Of Ages found</Translate>
            </div>
          )
        )}
      </div>
      {totalItems ? (
        <div className={ageBySingleYearOfAgeList && ageBySingleYearOfAgeList.length > 0 ? '' : 'd-none'}>
          <div className="justify-content-center d-flex">
            <JhiItemCount page={paginationState.activePage} total={totalItems} itemsPerPage={paginationState.itemsPerPage} i18nEnabled />
          </div>
          <div className="justify-content-center d-flex">
            <JhiPagination
              activePage={paginationState.activePage}
              onSelect={handlePagination}
              maxButtons={5}
              itemsPerPage={paginationState.itemsPerPage}
              totalItems={totalItems}
            />
          </div>
        </div>
      ) : (
        ''
      )}
    </div>
  );
};

export default AgeBySingleYearOfAge;
